import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Eye, EyeOff, Trash2, Copy, Edit } from 'lucide-react';
import { passwordService } from '../../services/passwordService';
import { clientService } from '../../services/clientService';
import { useToast } from '../../hooks/use-toast';

const Senhas = () => {
  const { toast } = useToast();
  const [passwords, setPasswords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPassword, setEditingPassword] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    category: '',
    client_id: '',
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    category: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [passwordsData, clientsData] = await Promise.all([
        passwordService.getAll(),
        clientService.getAll()
      ]);
      setPasswords(passwordsData);
      setClients(clientsData);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast({
        title: 'Erro ao carregar senhas',
        description: 'Não foi possível carregar as senhas.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client ? client.name : 'Desconhecido';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await passwordService.create(formData);
      toast({
        title: 'Senha criada',
        description: 'A senha foi criada com sucesso.',
      });
      setDialogOpen(false);
      setFormData({
        title: '',
        username: '',
        password: '',
        url: '',
        category: '',
        client_id: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao criar senha:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível criar a senha.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (password) => {
    setEditingPassword(password);
    setEditFormData({
      title: password.title,
      username: password.username,
      password: password.password,
      url: password.url || '',
      category: password.category || '',
      notes: password.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await passwordService.update(editingPassword._id, editFormData);
      toast({
        title: 'Senha atualizada',
        description: 'A senha foi atualizada com sucesso.',
      });
      setEditDialogOpen(false);
      setEditingPassword(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar senha:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Não foi possível atualizar a senha.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta senha?')) return;
    
    try {
      await passwordService.delete(id);
      toast({
        title: 'Senha excluída',
        description: 'A senha foi excluída com sucesso.',
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir senha:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir a senha.',
        variant: 'destructive',
      });
    }
  };

  const toggleShowPassword = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copiado!',
      description: `${label} copiado para a área de transferência.`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciador de Senhas</h1>
          <p className="text-gray-600 mt-1">Armazene senhas de clientes com segurança</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
              <Plus className="w-4 h-4 mr-2" />
              Nova Senha
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nova Senha</DialogTitle>
              <DialogDescription>Adicione uma nova senha ao gerenciador</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Categoria</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Ex: Sistema, Email, etc"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Usuário *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://exemplo.com (opcional)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_id">Cliente *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
                  Salvar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Password Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Senha</DialogTitle>
            <DialogDescription>Atualize as informações da senha</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_title">Título *</Label>
                <Input
                  id="edit_title"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_category">Categoria</Label>
                <Input
                  id="edit_category"
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  placeholder="Ex: Sistema, Email, etc"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_username">Usuário *</Label>
                <Input
                  id="edit_username"
                  value={editFormData.username}
                  onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_password">Senha *</Label>
                <Input
                  id="edit_password"
                  type="password"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_url">URL</Label>
              <Input
                id="edit_url"
                type="url"
                value={editFormData.url}
                onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
                placeholder="https://exemplo.com (opcional)"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_notes">Observações</Label>
              <Textarea
                id="edit_notes"
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#4A2B7F] hover:bg-[#6B46C1]">
                Atualizar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Senhas Armazenadas</CardTitle>
          <CardDescription>Gerencie todas as senhas dos clientes</CardDescription>
          
          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <div className="flex-1">
              <Label>Filtrar por Cliente</Label>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client._id} value={client._id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Filtrar por Categoria</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {[...new Set(passwords.map(p => p.category).filter(Boolean))].map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : passwords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma senha cadastrada ainda
            </div>
          ) : (
            <div className="space-y-4">
              {passwords
                .filter(p => filterClient === 'all' || p.client_id === filterClient)
                .filter(p => filterCategory === 'all' || p.category === filterCategory)
                .map((password) => (
                <Card key={password._id} className="border-l-4 border-l-[#4A2B7F]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold text-lg">{password.title}</h3>
                          {password.category && (
                            <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                              {password.category}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Cliente:</span>{' '}
                            <span className="font-medium">{getClientName(password.client_id)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Usuário:</span>{' '}
                            <span className="font-medium">{password.username}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="ml-1 h-6 w-6 p-0"
                              onClick={() => copyToClipboard(password.username, 'Usuário')}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">Senha:</span>{' '}
                            <span className="font-mono">
                              {showPasswords[password._id] ? password.password : '••••••••'}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleShowPassword(password._id)}
                            >
                              {showPasswords[password._id] ? (
                                <EyeOff className="w-3 h-3" />
                              ) : (
                                <Eye className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => copyToClipboard(password.password, 'Senha')}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          {password.url && (
                            <div>
                              <span className="text-gray-600">URL:</span>{' '}
                              <a
                                href={password.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {password.url}
                              </a>
                            </div>
                          )}
                        </div>
                        {password.notes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Observações:</span> {password.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col space-y-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleEdit(password)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(password._id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Senhas;
